import { useCallback, useEffect, useRef, useState } from "react";

import {
  createContinuousDictationController,
  isSpeechRecognitionSupported,
} from "@/utils/speechDictation.js";

/**
 * Hook dictée vocale BT-040 — mode continu jusqu'au stop utilisateur.
 */
export function useSpeechDictation(locale, draftText, setDraftText) {
  const [voiceSupported] = useState(() => isSpeechRecognitionSupported());
  const [dictating, setDictating] = useState(false);
  const controllerRef = useRef(null);
  const draftTextRef = useRef(draftText);

  useEffect(() => {
    draftTextRef.current = draftText;
  }, [draftText]);

  useEffect(() => {
    controllerRef.current?.stop();
    controllerRef.current = createContinuousDictationController({
      locale,
      getBaseText: () => draftTextRef.current.trim(),
      onText: (text) => {
        draftTextRef.current = text;
        setDraftText((d) => ({ ...d, text }));
      },
      onActiveChange: setDictating,
    });
    return () => controllerRef.current?.stop();
  }, [locale, setDraftText]);

  const stopDictation = useCallback(() => {
    controllerRef.current?.stop();
    setDictating(false);
  }, []);

  const startDictation = useCallback(() => {
    if (!voiceSupported) return;
    draftTextRef.current = draftText.trim();
    controllerRef.current?.start();
  }, [draftText, voiceSupported]);

  useEffect(
    () => () => {
      controllerRef.current?.stop();
    },
    [],
  );

  return { voiceSupported, dictating, startDictation, stopDictation };
}
